import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ShieldAlert, CheckCircle2, AlertTriangle, HeartPulse,
  TrendingUp, Scale, Coins, Rocket, Quote, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { getCompany, addHolding } from "../lib/api";
import { Panel, SectionHeading, SectionDivider } from "../components/Panel";
import { TermExplainer } from "../components/TermExplainer";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { CompanyHero } from "../components/company/CompanyHero";
import { ScorePanel } from "../components/company/ScorePanel";
import { PriceChart } from "../components/company/PriceChart";
import { TrendsChart } from "../components/company/TrendsChart";
import { WhatChanged } from "../components/company/WhatChanged";
import { AskLongview } from "../components/company/AskLongview";
import { riskLevelClass } from "../lib/format";
import { DataState } from "../components/DataState";
import { SkeletonBlock } from "../components/LoadingSkeleton";

export default function Company() {
  const { ticker } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setC(null); setNotFound(false); setLoadError(false);
    getCompany(ticker).then(setC).catch((error) => { if (error?.response?.status === 404) setNotFound(true); else setLoadError(true); });
  }, [ticker]);

  if (notFound) return <NotFound ticker={ticker} />;
  if (loadError) return <DataState title="Company intelligence is temporarily unavailable" detail="LONGVIEW could not retrieve this company's verified data. No substitute figures are being shown." onRetry={() => window.location.reload()} />;
  if (!c) return <CompanySkeleton />;

  const extra = (c.financial_snapshot || []).filter((m) => ["net_margin", "roce", "cash"].includes(m.key));
  const gridMetrics = [...c.metrics, ...extra];

  const addToPortfolio = async () => {
    try {
      await addHolding({ ticker: c.ticker, quantity: 5, avg_price: c.price });
      toast.success(`Added 5 × ${c.ticker} to your portfolio simulator`, { description: "This is a simulation — no real money involved." });
    } catch (e) {
      toast.error("Couldn't add to portfolio simulator");
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Intelligence Header */}
      <CompanyHero c={c} onAddToPortfolio={addToPortfolio} />

      {/* Price Performance — the visual centerpiece */}
      <Reveal><PriceChart ticker={c.ticker} initial={c.prices} /></Reveal>

      <SectionDivider className="my-2" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {/* Company overview */}
          <Reveal>
            <Panel data-testid="company-what-it-does-section" className="p-5 sm:p-6">
              <SectionHeading kicker="Company Overview" title="What does this company do?" icon={Eye} />
              <div className="space-y-3">
                {c.what_it_does.map((p, i) => <p key={i} className="text-sm leading-relaxed text-zinc-300">{p}</p>)}
              </div>
              <p className="mt-4 border-l-2 border-amber-500/40 pl-3 font-serif text-base italic text-zinc-300">
                {c.summary}
              </p>
            </Panel>
          </Reveal>

          {/* How it makes money */}
          <Reveal>
            <Panel data-testid="company-how-it-makes-money-section" className="p-5 sm:p-6">
              <SectionHeading kicker="Revenue Model" title="How does it make money?" icon={Coins} />
              <div className="space-y-3">
                {c.revenue_streams.map((r) => (
                  <div key={r.name}>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-white">{r.name}</span>
                      <span className="font-mono text-sm text-amber-300">{r.percent}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-zinc-800/70">
                      <div className="h-full rounded-full bg-gradient-to-r from-amber-500/70 to-amber-400/90 transition-[width] duration-700" style={{ width: `${r.percent}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">{r.detail}</p>
                  </div>
                ))}
              </div>
            </Panel>
          </Reveal>

          {/* Key intelligence metrics */}
          <Reveal>
            <Panel data-testid="company-business-metrics-grid" className="p-5 sm:p-6">
              <SectionHeading kicker="Key Intelligence" title="The business at a glance" />
              <StaggerGrid className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {gridMetrics.map((m) => (
                  <StaggerItem key={m.key}>
                    <div data-testid={`company-metric-${metricTestId(m.key)}`} className="rounded-lg border border-white/[0.07] bg-[#0B0C0E] p-3.5 transition-colors hover:border-white/15">
                      <div className="mb-1 flex items-center justify-between gap-1">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{m.term}</span>
                        <TermExplainer term={m.term} what={m.what} why={m.why} testId={`company-metric-explain-${metricTestId(m.key)}`} />
                      </div>
                      <div className="font-mono text-lg font-semibold text-white tabular-nums">{m.value}</div>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerGrid>
            </Panel>
          </Reveal>

          {/* Trends & analysis */}
          <Reveal><TrendsChart trends={c.trends} /></Reveal>

          {/* Verified company events */}
          <Reveal><WhatChanged ticker={c.ticker} /></Reveal>

          {/* Catalysts */}
          {c.catalysts?.length > 0 && (
            <Reveal>
              <Panel data-testid="company-catalysts-section" className="p-5 sm:p-6">
                <SectionHeading kicker="Catalysts" title="What could drive the business forward" icon={Rocket} />
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {c.catalysts.map((cat, i) => (
                    <div key={i} className="flex gap-2.5 rounded-md border border-emerald-500/12 bg-emerald-500/[0.03] p-3">
                      <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                      <p className="text-xs leading-relaxed text-zinc-300">{cat}</p>
                    </div>
                  ))}
                </div>
              </Panel>
            </Reveal>
          )}

          {/* What could go wrong */}
          <Reveal>
            <div data-testid="company-what-could-go-wrong-section" className="rounded-lg border border-rose-500/25 bg-gradient-to-b from-rose-950/20 to-transparent p-5 sm:p-6">
              <div className="mb-1 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-rose-400" />
                <h2 className="text-lg font-semibold tracking-tight text-white sm:text-xl">What could go wrong?</h2>
              </div>
              <p className="mb-4 text-xs leading-relaxed text-rose-200/70">
                Every investment carries risk. A strong business is not automatically a good investment at any price.
                Understand these before you consider buying.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {c.what_could_go_wrong.map((r) => (
                  <div key={r.category} className="rounded-md border border-white/[0.07] bg-[#0B0C0E] p-3.5">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-white">{r.category}</span>
                      <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide ${riskLevelClass(r.level)}`}>{r.level}</span>
                    </div>
                    <p className="text-xs leading-relaxed text-zinc-400">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Side column — LONGVIEW Intelligence */}
        <div className="space-y-6 lg:col-span-4">
          <div className="kicker">LONGVIEW Intelligence</div>

          {/* Why it's here */}
          <Reveal>
            <Panel data-testid="company-why-its-here" accent="amber" className="p-5">
              <div className="kicker mb-2 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5 text-amber-400" /> Why it's here</div>
              <p className="text-sm leading-relaxed text-zinc-200">{c.discover_reason}.</p>
              <p className="mt-2 text-xs leading-relaxed text-zinc-400">{c.why_it_matters}</p>
            </Panel>
          </Reveal>

          <Reveal><ScorePanel scores={c.scores} /></Reveal>

          <Reveal>
            <Panel className="p-5">
              <div className="kicker mb-3">LONGVIEW Analysis</div>
              <div data-testid="company-longview-financial-health" className="mb-4"><AnalysisRow icon={HeartPulse} label="Financial Health" node={c.analysis.financial_health} /></div>
              <div className="mb-4"><AnalysisRow icon={TrendingUp} label="Growth" node={c.analysis.growth} /></div>
              <div><AnalysisRow icon={Scale} label="Valuation" node={c.analysis.valuation} /></div>
            </Panel>
          </Reveal>

          <Reveal>
            <Panel data-testid="company-longview-strengths" className="p-5">
              <div className="kicker mb-3 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Business Strengths</div>
              <ul className="space-y-2.5">
                {c.strengths.map((s, i) => (
                  <li key={i} className="flex gap-2 text-xs leading-relaxed text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{s}</li>
                ))}
              </ul>
            </Panel>
          </Reveal>

          <Reveal>
            <Panel data-testid="company-longview-risks" className="p-5">
              <div className="kicker mb-3 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Key Risks</div>
              <ul className="space-y-2.5">
                {c.risks.map((s, i) => (
                  <li key={i} className="flex gap-2 text-xs leading-relaxed text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />{s}</li>
                ))}
              </ul>
            </Panel>
          </Reveal>
        </div>
      </div>

      <SectionDivider className="my-2" />

      {/* Ask LONGVIEW */}
      <Reveal><AskLongview ticker={c.ticker} questions={c.analyst_questions || []} /></Reveal>

      {/* The Longview */}
      <Reveal>
        <div data-testid="company-the-longview" className="lv-panel lv-top-edge relative overflow-hidden p-6 sm:p-8">
          <Quote className="absolute right-6 top-6 h-10 w-10 text-amber-500/10" />
          <div className="kicker mb-3 text-amber-400">The LONGVIEW</div>
          <p className="max-w-3xl font-serif text-lg leading-relaxed text-zinc-100 sm:text-xl">{c.longview_summary}</p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-600">LONGVIEW synthesis · educational, not investment advice</p>
        </div>
      </Reveal>
    </div>
  );
}

const AnalysisRow = ({ icon: Icon, label, node }) => (
  <div>
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-sm font-medium text-white"><Icon className="h-3.5 w-3.5 text-amber-400" />{label}</span>
      <span className="rounded border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-amber-300">{node.status}</span>
    </div>
    <p className="mt-1.5 text-xs leading-relaxed text-zinc-400">{node.text}</p>
  </div>
);

const metricTestId = (key) => {
  const map = { revenue: "revenue", net_profit: "profit", pe_ratio: "pe", debt_to_equity: "debt", market_cap: "marketcap", roe: "roe", revenue_growth: "revgrowth", operating_margin: "margin", net_margin: "netmargin", roce: "roce", cash: "cash" };
  return map[key] || key;
};

const CompanySkeleton = () => (
  <div className="space-y-6">
    <SkeletonBlock className="h-[220px]" />
    <SkeletonBlock className="h-[420px]" />
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-8">
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-56" />
      </div>
      <div className="space-y-6 lg:col-span-4">
        <SkeletonBlock className="h-64" />
        <SkeletonBlock className="h-40" />
      </div>
    </div>
  </div>
);

const NotFound = ({ ticker }) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-white/[0.07] bg-[#0B0C0E] py-24 text-center">
    <h2 className="text-lg font-semibold text-white">Company not found</h2>
    <p className="mt-1 text-sm text-zinc-500">"{ticker}" isn't in the current LONGVIEW coverage.</p>
    <Link to="/companies" className="mt-4 rounded-md border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:border-white/25">Browse companies</Link>
  </div>
);
